import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Toolbar from '../../common/Toolbar';
import ProductFilter from '../../filters/ProductFilter';
import styles from './DropshipProducts.module.scss';
import ActionLinkTop from '../../common/ActionLinkTop';
import ThemeFilterRetailer from '../../../components/theme/ThemeFilterRetailer';
import Search from '../../common/Search';
import ThemeRetailer from '../../../components/theme/ThemeRetailer';
import WrapperColumnRetailers from '../../../components/grid-container/WrapperColumnRetailers';
import DropshipProductCard from '../../common/DropshipProductCard';
import Select from '../../common/Select';
import Button from '../../common/Button';
import Wall from '../../common/Wall';
import Spinner from '../../common/Spinner';
import { StaticFilters } from '../../../constants/staticFilters';
import { useSelector } from 'react-redux';
import { ActiveStatus } from '../../../constants/activeStatus';
import { MAX_ITEMS_TO_SELECT_ALL } from '../../common/SelectAllModal/consts';
import { BlockSelectAllModal } from '../../common/SelectAllModal';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTh } from '@fortawesome/free-solid-svg-icons';
import cn from 'classnames';
import { InfoTitle } from '../../common/InfoTooltip/InfoTitle';
import ProductsListView from '../ProductsListView';
import {
  enrichVariantsWithPriceDataForRetailerProductsListUi,
  getMandatoryCurrencyFromRetailer,
} from '../../../utils/pricings';
import { dropshipProductsUtils } from '../../../utils/dropship-products';
import history from '../../../utils/history';
import { getRetailerProductApprovedStatusForVariants } from '../../../utils/products-permissions';

const LIST_VIEW = 'list';
const GRID_VIEW = 'grid';

function DropshipProducts(props) {
  const { isLoadingProducts = false, checked = {} } = props;

  const [viewMode, setViewMode] = useLocalStorage('DropshipProductsView', GRID_VIEW);

  const { retailer } = useSelector((state) => state.role);

  const retailerMandatoryCurrency = getMandatoryCurrencyFromRetailer(retailer);

  const uiProducts = props.data.map((product) => ({
    ...product,
    variants: enrichVariantsWithPriceDataForRetailerProductsListUi({
      variants: product.variants,
      retailerMandatoryCurrency,
    }),
  }));

  const hasAutoSync = useMemo(() => {
    const currentBrand = props.brands.find((brand) => brand.value === props.supplierId);
    return currentBrand ? currentBrand.auto_sync : false;
  }, [props.brands, props.supplierId]);
  const [shouldOpenSelectAllModal, setShouldOpenSelectAllModal] = useState(false);

  const onCheckProduct = (productChecked, value) => {
    const { check, uncheck } = props;

    if (productChecked) {
      check(value);
    } else {
      uncheck(value);
    }
  };

  const isChecked = (model) => checked.hasOwnProperty(model);
  const { currentFilters } = useSelector((state) => state.productFilters);
  const { count } = useSelector((state) => state.dropshipProducts);

  const isFilteringArchived = useMemo(() => {
    return currentFilters?.activeProducts === ActiveStatus.ARCHIVED;
  }, [currentFilters]);

  const shouldDisableAction = Object.keys(props.checked).length === 0 || isFilteringArchived;

  const handlePageClick = (data) => {
    const { perPage, setPage } = props;
    setPage(perPage * data.selected);
  };

  const handleSelectAll = () => {
    if (count > MAX_ITEMS_TO_SELECT_ALL) {
      setShouldOpenSelectAllModal(true);
    } else props.selectAll();
  };

  const selectAllOnPage = () => {
    const selected = {};
    props.data.forEach((item) => {
      selected[item.model] = item.variants;
    });
    props.selectAllOnPage(selected);
  };

  const redirectToProductPage = (product) => {
    const productLink = dropshipProductsUtils.getCatalogueManagementProductUrl({
      retailerId: props.retailerId,
      model: product.model,
      params: `/${props.supplierId}`,
    });

    history.push(productLink);
  };

  const renderProducts = () => {
    if (props.isLoading) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }

    if (!props.data?.length) {
      return <div className={styles.noProducts}>No Products</div>;
    }

    if (viewMode === LIST_VIEW) {
      return (
        <ProductsListView
          products={uiProducts}
          order={props.order}
          orderBy={props.orderBy}
          setOrder={props.setOrder}
          open={redirectToProductPage}
          permissionType={'dropship'}
          getApprovedStatusForVariants={(product) => {
            return getRetailerProductApprovedStatusForVariants({ product });
          }}
          isChecked={isChecked}
          onChecked={onCheckProduct}
          shouldAllowUserToDoActions={true}
          withPriceData={true}
          withVariantBuffer={true}
        />
      );
    }

    return uiProducts.map((product) => (
      <ThemeRetailer key={product.model}>
        <DropshipProductCard
          product={product}
          checkbox={!hasAutoSync}
          onCheck={onCheckProduct}
          checked={isChecked(product.model)}
          approvedStatus={getRetailerProductApprovedStatusForVariants({ product })}
          open={props.openModal}
          params={`/${props.supplierId}`}
          retailerId={props.retailerId}
          slider={true}
        />
      </ThemeRetailer>
    ));
  };

  const approveButtons = () => (
    <>
      <Wall />
      <ActionLinkTop title="Select this page" onClick={selectAllOnPage} />
      <ActionLinkTop title="Select all" onClick={() => handleSelectAll()} />
      <ActionLinkTop title="Unselect all" onClick={props.unselectAll} />
      <Wall />
      <Button text="Approve" onClick={props.approveChecked} disabled={shouldDisableAction} />
      <Button margin_left color="white" text="Revoke" onClick={props.unapproveChecked} disabled={shouldDisableAction} />
    </>
  );

  const toggleViewButton = () => (
    <InfoTitle content={'Switch to ' + (viewMode === LIST_VIEW ? 'grid' : 'list') + ' view'}>
      <span
        className={cn(styles.toggleView, viewMode === LIST_VIEW ? 'ic-dashboard' : 'ic-menu')}
        title="Toggle View"
        onClick={() => setViewMode(viewMode === LIST_VIEW ? GRID_VIEW : LIST_VIEW)}
      >
        {viewMode === LIST_VIEW && <FontAwesomeIcon icon={faTh} />}
      </span>
    </InfoTitle>
  );

  const actionLinks = () => {
    return (
      <>
        {hasAutoSync ? null : approveButtons()}
        <Wall />
        {toggleViewButton()}
      </>
    );
  };

  const checkedCount = Object.keys(props.checked).length;

  const staticFilters = [StaticFilters.HAS_INVENTORY];
  if (!hasAutoSync) staticFilters.push(StaticFilters.IS_RETAILER_APPROVED);

  return (
    <>
      <div className={styles.wrapperToolbar}>
        <Toolbar grow name={hasAutoSync ? 'Products on your website' : 'Sync products to your website'}>
          <div className={styles.right}>
            {checkedCount > 0 && (
              <span className={styles.selectedCount}>
                {checkedCount} {checkedCount === 1 ? 'Product' : 'Products'} Selected
              </span>
            )}
            {actionLinks()}
          </div>
        </Toolbar>
        <div className={styles.isHide}>
          <div className="row vertical-indent">
            <ThemeFilterRetailer>
              <h4>Select Seller:</h4>
              <div className={styles.selectWrapper}>
                <Select
                  options={props.brands
                    .filter((item) => !!item.approved_for_ecommerce)
                    .map(({ value, label, imageUrl }) => ({ value: value.toString(), label, imageUrl }))}
                  value={props.supplierId.toString()}
                  onChange={props.setSupplierId}
                />
              </div>

              <Search searchQuery={props.searchQuery} search={props.productSearch} />

              <ProductFilter
                filters={props.filters}
                staticFilters={staticFilters}
                currentFilters={props.currentFilters}
                currentFilterSearches={props.currentFilterSearches}
                setFilter={props.setFilter}
                setFilterSearch={props.setFilterSearch}
                connectionHasAutoSync={hasAutoSync}
              />
            </ThemeFilterRetailer>

            <WrapperColumnRetailers
              isLoadingProducts={isLoadingProducts}
              currentPage={props.offset / props.perPage}
              pageCount={props.pageCount}
              handlePageClick={handlePageClick}
            >
              {renderProducts()}
            </WrapperColumnRetailers>
          </div>
        </div>
      </div>
      <BlockSelectAllModal
        open={shouldOpenSelectAllModal}
        onClose={() => setShouldOpenSelectAllModal(false)}
        retailerPage={true}
      />
    </>
  );
}

export default DropshipProducts;

DropshipProducts.propTypes = {
  perPage: PropTypes.number.isRequired,
  filters: PropTypes.object,
  data: PropTypes.array.isRequired,
  pageCount: PropTypes.number.isRequired,
  approveChecked: PropTypes.func.isRequired,
  unapproveChecked: PropTypes.func.isRequired,
  productSearch: PropTypes.func.isRequired,
  check: PropTypes.func,
  uncheck: PropTypes.func,
  checked: PropTypes.object,
  setPage: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  offset: PropTypes.number.isRequired,
  brands: PropTypes.array.isRequired,
  setSupplierId: PropTypes.func.isRequired,
  selectAllOnPage: PropTypes.func.isRequired,
  selectAll: PropTypes.func.isRequired,
  unselectAll: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  isLoadingProducts: PropTypes.bool,
  supplierId: PropTypes.number.isRequired,
  retailerId: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
};
